import { observable, computed, makeObservable } from 'mobx';
import { createTransformer } from 'mobx-utils';

import { EntityList } from '@vromo/models';
import { Group } from '@vromo/models';

export default class GroupStore {
    groups: EntityList<Group> = new EntityList(Group);
    loaded = false;

    constructor() {
        makeObservable(this, {
            groups: observable,
            loaded: observable,
            childDictionary: computed,
            sortedGroups: computed
        });
    }

    get childDictionary(): Map<number, Array<number>> {
        const dict = new Map<number, Array<number>>();

        // Populate Map with all group ids
        this.groups.forEach((g) => {
            if (g.id) {
                dict.set(g.id, []);
            }
        });

        // Populate direct child group ids
        this.groups.forEach((g) => {
            g.parents?.forEach((p) => {
                if (p.id && g.id && dict.get(p.id)) {
                    const children = dict.get(p.id);
                    dict.set(p.id, [...children!, g.id]);
                }
            });
        });

        // Populate all sub-child ids
        dict.forEach((children, parent) => {
            const newChildren = children;

            for (const childId of newChildren) {
                if (dict.get(childId) !== undefined) {
                    newChildren.push(...dict.get(childId)!);
                }
            }

            dict.set(parent, newChildren);
        });

        return dict;
    }

    get sortedGroups(): Group[] {
        if (!this.loaded)
            return []

        return this.groups.sortBy([(group: Group) => group.name!.toLowerCase()])
    }

    getTopLevelParent = (group: Group): Group | null => {
        const getParent = (g: Group): Group | null => {
            if (!g.parent)
                return g
            const parent = this.withId(g.parent)
            return parent ? getParent(parent!) : null
        }
        return getParent(group)
    }

    getChildren = (group: Group): Group[] => {
        const childrenIds = this.childDictionary.get(group.id!);
        const result = [];

        const childGroups = childrenIds?.length
            ? (childrenIds.map((id) => this.withId(id)).filter((g) => !!g) as Group[])
            : [];

        result.push(...childGroups);

        return result;
    };

    isChild = (parentId: number, childId: number): boolean =>
        this.childDictionary.has(parentId)
            ? this.childDictionary.get(parentId)!.includes(childId)
            : false;

    withId = createTransformer((group: number) => this.groups.get(group));
}
